/**
 * SfxDurationSlider Component
 *
 * Duration slider with the SERVER's cost quote beside it.
 *
 * Why the price is fetched and not computed
 * -----------------------------------------
 * This component used to price the clip itself, in TypeScript, with
 * `calculateSfxCost(provider, seconds)` -- `costPer10s / 10 * seconds`, a
 * straight line through the origin. The server does not charge on a straight
 * line: three of the four providers bill a flat per-generation fee PLUS a
 * per-second rate (`constants/sound_effects/*.py::calculate_*_cost`), so the two
 * numbers meet at exactly one point -- 10 s, the duration the per-10s constants
 * were written for -- and the fourth (ltx2_audio, which really is per-second)
 * still drifted by a token because the UI rounded up where the server truncates.
 * Measured across every stop the slider can produce, 113 of its 117 disagreed:
 *
 *   hunyuan_foley  29/30 stops wrong, worst 5.47x at 1 s (15 shown, 82 charged)
 *   audiocraft     29/30 stops wrong, worst 5.44x at 1 s ( 9 shown, 49 charged)
 *   stable_audio   46/47 stops wrong, worst 5.50x at 1 s ( 6 shown, 33 charged)
 *   ltx2_audio      9/10 stops wrong, rounding only      (20 shown, 19 charged)
 *
 * A price computed twice is the RULE #-2 duplication this campaign removed
 * everywhere else. So the slider asks POST /api/v1/cost/preview -- the same
 * estimator `POST /api/v1/sound-effects/generate/text` pre-charges through --
 * via the shared TokenEstimateCard, and there is one implementation of this
 * price again.
 */

import { memo, useMemo } from 'react';
import { Clock } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { type SfxProvider } from './SfxProviderSelector.constants';
import {
  getSfxBaseProvider,
  getSfxProviderLimits,
  isLocalProvider,
} from '../../constants/soundEffects';
import { TokenEstimateCard } from '../tokens/TokenEstimateCard';

// ============================================================================
// Operations
// ============================================================================

/**
 * The two operations this panel submits to, named exactly as
 * GET /api/v1/capabilities publishes them.
 *
 * The video-to-audio operation FIXES its provider (HunyuanFoley is the only
 * model that does V2A, and `VideoToAudioRequest` carries no provider field), so
 * its quote is asked with the duration alone -- sending a `provider` there would
 * be asking to be priced for something that route never reads.
 */
const SFX_TEXT_OPERATION_PATH = '/api/v1/sound-effects/generate/text';
const SFX_VIDEO_OPERATION_PATH = '/api/v1/sound-effects/generate/video-to-audio';

// ============================================================================
// Types
// ============================================================================

interface SfxDurationSliderProps {
  value: number;
  onChange: (seconds: number) => void;
  provider: SfxProvider;
  disabled?: boolean;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export const SfxDurationSlider = memo(function SfxDurationSlider({
  value,
  onChange,
  provider,
  disabled = false,
  className = '',
}: SfxDurationSliderProps) {
  const { t } = useTranslation();

  // Resolved for cloud AND "_local" ids. The old cloud-only lookup returned
  // undefined for every local provider and silently fell back to 10s, so
  // audiocraft_local (30s) and stable_audio_local (47s) were capped at 10s.
  const limits = useMemo(() => getSfxProviderLimits(provider), [provider]);
  const maxDuration = limits.maxDuration;

  // The cloud model behind the id, which is what the route prices: it strips
  // the "_local" suffix before calling SoundEffectsPricing and only then zeroes
  // the charge for a local variant. Asking the preview with the raw "_local" id
  // would be asking for a provider the price table does not have.
  const parameters = useMemo(
    () =>
      limits.requiresVideo
        ? { duration_seconds: value }
        : { provider: getSfxBaseProvider(provider), duration_seconds: value },
    [limits.requiresVideo, provider, value]
  );

  // Every SFX provider is a local GPU model. On desktop (Electron) a "_local"
  // id runs on the user's own GPU and the route charges 0; a cloud id runs on
  // Asgard's GPU and is billed. That is the provider's identity, not the
  // estimator's category flag (which says "this operation TYPE has a local
  // path" for every SFX request), so it is passed as an explicit override.
  const runsLocally = isLocalProvider(provider);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      onChange(Number(e.target.value));
    }
  };

  return (
    <div className={`form-group ${className}`}>
      <label className="form-label-icon">
        <Clock className="h-4 w-4 flex-shrink-0" />
        <span>{t('soundEffects.durationSlider.title', { defaultValue: 'Duration' })}</span>
      </label>

      {/* Duration Display and Cost */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-white">{value}</span>
          <span className="text-sm text-white/50">
            {t('soundEffects.durationSlider.seconds', { defaultValue: 'seconds' })}
          </span>
        </div>
        <TokenEstimateCard
          operationPath={limits.requiresVideo ? SFX_VIDEO_OPERATION_PATH : SFX_TEXT_OPERATION_PATH}
          parameters={parameters}
          isLocalProvider={runsLocally}
        />
      </div>

      {/* Slider */}
      <div className="relative">
        <input
          type="range"
          min="1"
          max={maxDuration}
          step="1"
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className={`
            w-full h-2 rounded-lg appearance-none cursor-pointer
            bg-black/30
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:w-4
            [&::-webkit-slider-thumb]:h-4
            [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-purple-500
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-webkit-slider-thumb]:shadow-lg
            [&::-webkit-slider-thumb]:shadow-purple-500/50
            [&::-moz-range-thumb]:w-4
            [&::-moz-range-thumb]:h-4
            [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:bg-purple-500
            [&::-moz-range-thumb]:cursor-pointer
            [&::-moz-range-thumb]:border-0
            [&::-moz-range-thumb]:shadow-lg
            [&::-moz-range-thumb]:shadow-purple-500/50
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          style={{
            background: `linear-gradient(to right, rgb(168 85 247) 0%, rgb(168 85 247) ${((value - 1) / (maxDuration - 1)) * 100}%, rgba(0,0,0,0.3) ${((value - 1) / (maxDuration - 1)) * 100}%, rgba(0,0,0,0.3) 100%)`,
          }}
        />
        {/* Min/Max Labels */}
        <div className="flex justify-between mt-1 text-xs text-white/40">
          <span>1s</span>
          <span>{maxDuration}s max</span>
        </div>
      </div>

      {/* Info */}
      <div className="mt-3 p-2 rounded-lg bg-black/20 border border-white/10">
        <p className="text-xs text-white/50">
          {t('soundEffects.durationSlider.info', {
            defaultValue: 'Provider max: {{max}}s. Longer durations cost more tokens.',
            max: maxDuration,
          })}
        </p>
      </div>
    </div>
  );
});

export default SfxDurationSlider;
